import type { VotePayload } from '@creator/sdk/modules/voting/voting.service';
import { purgePostCache } from '@src/store/posts/common/cache';
import { setPost } from '@src/store/posts/posts';
import { type Action, action, type Computed, computed, type Thunk, thunk } from 'easy-peasy';
import type { StoreModel } from '..';
import { getUserId } from '../user/helpers';
import { loadVote, vote } from './actions';
import { getMyVote } from './helpers';

export interface VotingModel {
    userVotes: { [postId: string]: number };
    vote: Thunk<VotingModel, VotePayload, null, StoreModel>;
    getMyVote: Computed<VotingModel, (postId: string) => number | undefined>;
    loadVote: Thunk<VotingModel, { postId: string }, null, StoreModel, Promise<any>>;
    setVote: Action<VotingModel, { postId: string; userVote: number }>;
}

const votingModel: VotingModel = {
    userVotes: {},

    vote: thunk(async (actions, payload, _helpers) => {
        const { postId, selectedOptionIndex } = payload;

        // Set user vote in store -
        // If user already voted for this option - set to -1 to unselect
        const myPrevVote = getMyVote(postId);
        if (myPrevVote !== selectedOptionIndex) actions.setVote({ postId: postId, userVote: selectedOptionIndex });
        else actions.setVote({ postId: postId, userVote: -1 });

        const res = await vote(payload);
        const { post } = res;
        // Update post in store
        setPost(post);
        // Purge post cache to ensure SSR and cached pages get updated vote data
        purgePostCache(post);
        return res;
    }),

    loadVote: thunk(async (actions, payload) => {
        const { postId } = payload;
        const userId = getUserId();
        const vote = await loadVote(postId);
        if (vote && vote.votes[userId] !== undefined) actions.setVote({ postId: postId, userVote: vote.votes[userId] });
    }),

    setVote: action((state, { postId, userVote }) => {
        state.userVotes[postId] = userVote;
    }),

    getMyVote: computed(state => postId => state.userVotes[postId]),
};

export default votingModel;
